import { useMemo } from "react";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../config/uiLayoutConfig";
import { getNavIcon } from "../config/navIconRegistry";
import { usePageLayout } from "../context/LayoutContext";
import { useUser } from "../pages/login/UserContextPersistent";
import { buildFleetNavFromLayout, createHasPermission, filterNavForInactiveOrganization } from "../layout/navLayoutUtils";
import { hasLayoutPermission } from "../config/vehiclesModuleCatalog";

const LEGACY_SCHOOL_NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", path: "/school", iconKey: "school.nav.dashboard" },
  {
    id: "students",
    label: "Students",
    path: "/school-students",
    iconKey: "school.nav.students",
    permission: { apiPath: "/api/student", action: "GET" },
  },
  {
    id: "vehicles",
    label: "Vehicles",
    path: "/school-vehicles",
    iconKey: "school.nav.vehicles",
    permission: { apiPath: "/api/vehicle", action: "GET" },
  },
  {
    id: "attendants",
    label: "Attendants",
    path: "/school-attendants",
    iconKey: "school.nav.attendants",
    permission: { apiPath: "/api/employee", action: "GET" },
  },
  {
    id: "routes",
    label: "Routes",
    path: "/school-routes",
    iconKey: "school.nav.routes",
    permission: { apiPath: "/api/route", action: "GET" },
  },
  {
    id: "stops",
    label: "Stops",
    path: "/school-stops",
    iconKey: "school.nav.stops",
    permission: { apiPath: "/api/stop", action: "GET" },
  },
  {
    id: "drivers",
    label: "Drivers",
    path: "/school-drivers",
    iconKey: "school.nav.drivers",
    permission: { apiPath: "/api/driver", action: "GET" },
  },
  {
    id: "schedules",
    label: "Schedules",
    path: "/school-schedules",
    iconKey: "school.nav.schedules",
    permission: { apiPath: "/api/schedule", action: "GET" },
  },
  {
    id: "users",
    label: "Users",
    path: "/school-users",
    iconKey: "school.nav.users",
    permission: { apiPath: "/api/user", action: "GET" },
  },
  {
    id: "sites",
    label: "Sites",
    path: "/school-sites",
    iconKey: "school.nav.sites",
    permission: { apiPath: "/api/site", action: "GET" },
  },
  {
    id: "analytics",
    label: "Analytics",
    path: "/school-reports",
    iconKey: "school.nav.analytics",
    permission: { apiPath: "/api/managedata/reports", action: "GET" },
  },
  {
    id: "settings",
    label: "Settings",
    path: "/school-settings",
    iconKey: "school.nav.settings",
    permission: { apiPath: "/api/settings", action: "GET" },
  },
  {
    id: "alerts",
    label: "Alerts",
    path: "/school-alerts",
    iconKey: "school.nav.alert",
    showBadge: true,
    permission: { apiPath: "/api/alert", action: "GET" },
  },
];

function getLegacySchoolNavItems(hasPermission) {
  return LEGACY_SCHOOL_NAV_ITEMS.filter((item) => {
    if (!item.permission) {
      return true;
    }

    return hasLayoutPermission(item.permission, hasPermission);
  }).map((item) => ({
    id: item.id,
    path: item.path,
    label: item.label,
    icon: getNavIcon(item.iconKey, item.label),
    showBadge: Boolean(item.showBadge),
    onClick: null,
  }));
}

const noopResolveAction = () => null;

export function useSchoolNavLayout() {
  const { state, organizationInactive } = useUser();
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const hasPermission = useMemo(
    () =>
      createHasPermission(state?.features, {
        writesDisabled: organizationInactive,
      }),
    [state?.features, organizationInactive],
  );

  const legacyNavData = useMemo(
    () => getLegacySchoolNavItems(hasPermission),
    [hasPermission],
  );
  const { layout: navLayout } = usePageLayout(useJsonLayout ? PAGE_IDS.SCHOOL_NAV : null);
  const currentUser = state?.user || {};
  const roleName = String(
    currentUser?.role?.name || currentUser?.role || state?.role?.name || state?.role || "",
  ).toLowerCase();
  const roleId =
    currentUser?.roleId || state?.roleId || currentUser?.role?.id || state?.role?.id;
  const isSuperAdmin =
    currentUser?.email === "sales@routeeye.io" ||
    Number(roleId) === 3 ||
    roleName.includes("superadmin") ||
    roleName.includes("super admin");
  const roleKey = isSuperAdmin ? "SUPER_ADMIN" : "ORG_ADMIN";

  const jsonNavData = useMemo(() => {
    if (!useJsonLayout || !navLayout) {
      return null;
    }

    return filterNavForInactiveOrganization(
      buildFleetNavFromLayout(navLayout, {
        hasPermission,
        resolveAction: noopResolveAction,
        roleKey,
      }),
      organizationInactive,
    );
  }, [useJsonLayout, navLayout, hasPermission, roleKey, organizationInactive]);

  const visibleLegacyNavData = useMemo(
    () => filterNavForInactiveOrganization(legacyNavData, organizationInactive),
    [legacyNavData, organizationInactive],
  );

  return useJsonLayout ? jsonNavData ?? [] : visibleLegacyNavData;
}
